/* Magnifissance 2026 — cart drawer
 *
 * Replaces the 1.0 theme's jQuery + JSONP drawer. Every state change goes
 * through Shopify's Cart AJAX API, and the markup is re-rendered server-side
 * via the Section Rendering API — no HTML is built in JavaScript.
 */
(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
    }

    onClick(event) {
      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        close();
        return;
      }

      const stepper = event.target.closest('[data-cart-qty]');
      if (!stepper) return;

      const line = stepper.closest('[data-cart-line]');
      if (!line) return;
      event.preventDefault();
      changeLine(line.dataset.cartLine, stepper.dataset.cartQty);
    }

    onChange(event) {
      const input = event.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const line = input.closest('[data-cart-line]');
      if (!line) return;
      changeLine(line.dataset.cartLine, Math.max(0, parseInt(input.value, 10) || 0));
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------- open / close ---------- */

  let lastFocused = null;

  function el() {
    return document.getElementById('CartDrawer');
  }

  function open() {
    const drawer = el();
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    const panel = drawer.querySelector('.cart-drawer__panel');
    if (panel) {
      panel.setAttribute('aria-hidden', 'false');
      // Wait for the transition to start so focus doesn't cancel the slide-in.
      requestAnimationFrame(function () {
        panel.focus({ preventScroll: true });
      });
    }
  }

  function close() {
    const drawer = el();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    const panel = drawer.querySelector('.cart-drawer__panel');
    if (panel) panel.setAttribute('aria-hidden', 'true');
    if (lastFocused && document.contains(lastFocused)) lastFocused.focus({ preventScroll: true });
    lastFocused = null;
  }

  function setBusy(busy) {
    const drawer = el();
    if (drawer) drawer.classList.toggle('is-busy', busy);
  }

  /* ---------- data ---------- */

  // Pull the freshly rendered drawer out of a section payload and swap it in,
  // preserving the open state and re-syncing every header badge.
  function renderSection(html) {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById('CartDrawer');
    const current = el();
    if (!fresh || !current) return;

    const wasOpen = current.classList.contains('is-open');
    current.replaceWith(fresh);
    if (wasOpen) {
      fresh.classList.add('is-open');
      const panel = fresh.querySelector('.cart-drawer__panel');
      if (panel) panel.setAttribute('aria-hidden', 'false');
    }
    syncCount(parseInt(fresh.dataset.cartCount, 10) || 0);
  }

  function refresh(sections) {
    if (sections && sections[SECTION_ID]) {
      renderSection(sections[SECTION_ID]);
      return Promise.resolve();
    }
    return fetch(ROOT + '?sections=' + SECTION_ID, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (payload) { renderSection(payload[SECTION_ID]); });
  }

  function syncCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      if (node.tagName === 'CART-DRAWER') return;
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  function changeLine(line, quantity) {
    setBusy(true);
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    })
      .then(refresh)
      .catch(function () { window.location.href = ROOT + 'cart'; })
      .finally(function () { setBusy(false); });
  }

  /* ---------- add to cart ---------- */

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    // No drawer on the page (or JS-disabled markup) — let the browser post normally.
    if (!el()) return;

    event.preventDefault();

    const button = form.querySelector('[type="submit"], [name="add"]');
    if (button) button.setAttribute('aria-busy', 'true');
    setBusy(true);

    const body = new FormData(form);
    body.append('sections', SECTION_ID);

    fetch(ROOT + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || 'Add to cart failed');
          return data;
        });
      })
      .then(function (data) { return refresh(data.sections); })
      .then(open)
      .catch(function (error) {
        showError(form, error.message);
      })
      .finally(function () {
        if (button) button.removeAttribute('aria-busy');
        setBusy(false);
      });
  });

  function showError(form, message) {
    const target = form.querySelector('[data-cart-error]');
    if (!target) {
      window.alert(message);
      return;
    }
    target.textContent = message;
    target.hidden = false;
  }

  /* ---------- triggers ---------- */

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger || !el()) return; // href="/cart" still works without JS
    event.preventDefault();
    open();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    const drawer = el();
    if (drawer && drawer.classList.contains('is-open')) close();
  });

  // Let other scripts drive the drawer.
  window.CartDrawer = { open: open, close: close, refresh: refresh };
})();
